// Business Logic Layer - pure functions, no React. Mirrors
// _features/usageSources/modals/UsageSourceFormModal/utils/
// usageSourceFormRules.js's shape, including its schema post-processing
// trio (strip the one generic heading, fix the one broken placeholder,
// pack fields two-per-row) - same rationale as that file's own comments.

// Only "Basic details" is decorative chrome with no equivalent in the
// Companies Workspace visual language. "Calculation" (which also carries
// the Reset button - see useUsageCompilationFormSchema.js) and
// "Consumption Deduction on" are left alone; they're the only thing
// telling the user which group a given Select belongs to once the form
// has cascaded past the first section.
function stripGenericSectionHeading(sections) {
  return sections.map((section) =>
    section.section === "Basic details" ? { ...section, section: "" } : section
  );
}

// Confirmed via the live org-customization record (not guessed): "title"
// (a plain TextInput) has an explicit `placeholder: ""` with no engine
// fallback, so it rendered with no example text at all - same underlying
// bug class as "deductOn" below, just on a text field instead of a
// select. "Monthly API Rollup" is this org's own real compilation title
// (see its Edit-mode row). Every other Select-backed field
// (calculationType/table/sheet/billingUnit/usageSource/*Column) also
// carries `placeholder: ""`, but those all route through the legacy
// isObject/reference bridge (renderInput.jsx), which falls back to its
// own "Select" text regardless - only "deductOn" (a plain-string-items
// select, not a reference field) routes through the native Select
// component that the empty-string override actually breaks, same as
// Markets'/Billing Units'/Usage Sources' own Status field.
const FIELD_PLACEHOLDERS = {
  title: "Monthly API Rollup",
  deductOn: "Select",
};
function applyFieldPlaceholders(sections) {
  return sections.map((section) => ({
    ...section,
    fields: section.fields.map((field) =>
      FIELD_PLACEHOLDERS[field.name] !== undefined
        ? { ...field, placeholder: FIELD_PLACEHOLDERS[field.name] }
        : field
    ),
  }));
}

// Same "pack everything but a full-width field two-per-row" rule Markets'/
// Billing Units'/Usage Sources' own packFieldsForTwoColumnGrid follows,
// applied independently within every section. `usageSource` is treated as
// full-width alongside textArea fields - same reasoning as Usage
// Sources' own `originSource`: selecting it fires the cascading table/
// sheet/column lookup (see useUsageCompilationCascade.js), so it's the
// field that determines everything below it, not just another short
// input to pack next to Billing unit.
function packFieldsForTwoColumnGrid(sections) {
  const isFullWidth = (field) => field.type === "textArea" || field.name === "usageSource";
  return sections.map((section) => {
    const fields = section.fields || [];
    const fullWidthFields = fields.filter(isFullWidth).map((f) => ({ ...f, fullWidth: true }));
    const packedFields = fields.filter((f) => !isFullWidth(f));
    return { ...section, fields: [...packedFields, ...fullWidthFields] };
  });
}

/**
 * Applies every UsageCompilationFormModal-specific schema post-processing
 * rule to the schema (already resolved + cascade-augmented by
 * useUsageCompilationFormSchema.js).
 */
export function processUsageCompilationFormSchema(sections) {
  return stripGenericSectionHeading(packFieldsForTwoColumnGrid(applyFieldPlaceholders(sections)));
}

/**
 * Coerces a raw string value into the `{value, label}` object shape the
 * shared _components/form/ engine's isObject/valueSelector/valueDisplay
 * fields expect for display - ported verbatim from the old modal's
 * convertToSelectableObject, applied to table/sheet/column fields when
 * pre-filling an edit form from a raw API record.
 */
export function convertToSelectableObject(value) {
  if (!value) return "";
  if (typeof value === "object" && value.value) return value;
  return { value, label: value };
}

/**
 * Flattens a `{value, label}` object (or passes through a raw string)
 * back to the plain string the backend expects - ported verbatim from the
 * old modal's convertFromSelectableObject, applied to table/sheet/column
 * fields on submit.
 */
export function convertFromSelectableObject(value) {
  if (!value) return "";
  if (typeof value === "object" && value.value) return value.value;
  return value;
}

const COLUMN_FIELD_NAMES = [
  "table",
  "sheet",
  "consumptionColumn",
  "subscriptionIdColumn",
  "timestampColumn",
  "billingUnitIdColumn",
  "column",
];

/**
 * Applies convertFromSelectableObject to every table/sheet/column field
 * in a submitted form payload - the old modal did this inline in
 * handleSubmit for each of the 7 fields individually.
 */
export function flattenSelectableFields(validData) {
  const flattened = { ...validData };
  COLUMN_FIELD_NAMES.forEach((name) => {
    flattened[name] = convertFromSelectableObject(validData[name]);
  });
  return flattened;
}

// Same isObject/valueSelector/valueDisplay fields as COLUMN_FIELD_NAMES,
// plus calculationType (isObject: true via useResolveFormSchema's
// "calculationTypes" case, see useUsageCompilationFormSchema.js) - a
// usage compilation record stores all of these as raw strings, but
// useDynamicForm's buildDefaultValues copies rowData straight through, so
// without this conversion the Calculation section's Selects can never
// match a stored raw string against their `{value, label}` options and
// render blank on edit. Mirrors markets/utils/marketRowMapping.js's
// mapMarketRowForEdit.
export function mapUsageCompilationRowForEdit(rowData) {
  if (!rowData) return rowData;

  const mapped = { ...rowData, calculationType: convertToSelectableObject(rowData.calculationType) };
  COLUMN_FIELD_NAMES.forEach((name) => {
    mapped[name] = convertToSelectableObject(rowData[name]);
  });
  return mapped;
}

/**
 * Derives the create/update success-toast message from the submitted
 * form data - same "{title} - Usage compilation successfully
 * {created/updated}" rule the original inline handlers duplicated once
 * for create and once for edit.
 */
export function formatUsageCompilationSuccessMessage(validData, mode) {
  return `${validData?.title} - Usage compilation successfully ${mode === "create" ? "created" : "updated"}`;
}

export function getUsageSourceInspectionError(error) {
  if (!error) return "";
  return (
    error?.data?.message ||
    "Unable to inspect this usage source. Re-validate its connection and try again."
  );
}
